import React, { useEffect, useState } from 'react';
import ProductImage from '../ProductImage';
import { saveAllPrices, saveProductPricing } from '../../services/priceService';
import Icon from '../Icon';

const normalized = (value) =>
  value === '' || value == null || Number(value) === 0 ? null : Number(value);
export default function PricesTab({
  priceProducts,
  priceValues,
  setPriceValues,
  oldPriceValues,
  setOldPriceValues,
  expandedProducts,
  setExpandedProducts,
  calcRetail,
  seller,
  onDataChanged,
  onDirtyChange,
  loading,
}) {
  const [savedPrices, setSavedPrices] = useState({});
  const [savedOldPrices, setSavedOldPrices] = useState({});
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  useEffect(() => {
    setSavedPrices({});
    setSavedOldPrices({});
  }, [priceProducts]);
  const current = (variant) => priceValues[variant.id] ?? '';
  const baseline = (variant) =>
    Object.prototype.hasOwnProperty.call(savedPrices, variant.id)
      ? savedPrices[variant.id]
      : variant.price;
  const oldBaseline = (product) =>
    Object.prototype.hasOwnProperty.call(savedOldPrices, product.id)
      ? savedOldPrices[product.id]
      : product.oldPrice;
  const changedVariants = priceProducts
    .flatMap((p) => p.variants || [])
    .filter((v) => normalized(current(v)) !== normalized(baseline(v)));
  const changedProducts = priceProducts.filter(
    (p) => normalized(oldPriceValues[p.id]) !== normalized(oldBaseline(p))
  );
  const dirtyCount = changedVariants.length + changedProducts.length;
  const invalidPrice = priceProducts.some((p) =>
    (p.variants || []).some(
      (v) =>
        current(v) !== '' &&
        (!Number.isFinite(Number(current(v))) || Number(current(v)) < 0)
    )
  );
  const invalidOld = priceProducts.some((p) => {
    const old = oldPriceValues[p.id];
    const max = Math.max(
      0,
      ...(p.variants || []).map((v) =>
        normalized(current(v)) ? calcRetail(Number(current(v))) : 0
      )
    );
    return (
      old !== '' &&
      old != null &&
      (!Number.isFinite(Number(old)) || Number(old) < max)
    );
  });
  useEffect(() => onDirtyChange?.(dirtyCount > 0), [dirtyCount, onDirtyChange]);
  const save = async (productId) => {
    if (saving || invalidPrice || invalidOld) return;
    const selected = priceProducts.filter(
      (p) => !productId || p.id === productId
    );
    const ids = new Set(selected.flatMap((p) => p.variants.map((v) => v.id)));
    const updates = changedVariants
      .filter((v) => ids.has(v.id))
      .map((v) => ({ variantId: v.id, price: normalized(current(v)) }));
    const metadata = changedProducts.filter(
      (p) => !productId || p.id === productId
    );
    if (!updates.length && !metadata.length) return;
    setSaving(true);
    setNotice(null);
    try {
      if (updates.length) {
        await saveAllPrices(updates, seller.id);
        setSavedPrices((prev) => ({
          ...prev,
          ...Object.fromEntries(updates.map((v) => [v.variantId, v.price])),
        }));
      }
      for (const product of metadata) {
        const oldPrice = normalized(oldPriceValues[product.id]);
        await saveProductPricing(product.id, seller.id, {
          ...product,
          oldPrice,
        });
        setSavedOldPrices((prev) => ({ ...prev, [product.id]: oldPrice }));
      }
      setNotice({
        type: 'success',
        text: productId
          ? 'Цены товара сохранены'
          : 'Все изменения цен сохранены',
      });
    } catch (error) {
      setNotice({
        type: 'error',
        text:
          error.response?.data?.error ||
          'Не все изменения сохранены. Проверьте данные и повторите попытку; несохранённые поля остаются отмеченными.',
      });
    } finally {
      setSaving(false);
      onDataChanged?.();
    }
  };
  const reset = () => {
    setPriceValues(
      Object.fromEntries(
        priceProducts.flatMap((p) =>
          p.variants.map((v) => [v.id, baseline(v) ?? ''])
        )
      )
    );
    setOldPriceValues(
      Object.fromEntries(priceProducts.map((p) => [p.id, oldBaseline(p) ?? '']))
    );
    setNotice(null);
  };
  const visible = priceProducts.filter((p) =>
    `${p.title} ${p.publicArticle || p.platformSku} ${p.sellerSku}`
      .toLowerCase()
      .includes(search.toLowerCase())
  );
  return (
    <section className="seller-inventory">
      <div className="seller-page-heading">
        <div>
          <span className="eyebrow">ЦЕНЫ И СКИДКИ</span>
          <h1>Цены</h1>
          <p>
            Цена продавца и расчётная цена для покупателя. Пустая цена скрывает
            вариант с витрины.
          </p>
        </div>
      </div>
      <div className="seller-list-tools">
        <label className="seller-search">
          <Icon name="search" size={18} />
          <input
            aria-label="Поиск цен"
            placeholder="Название или артикул"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <span>{visible.length} товаров</span>
      </div>
      {notice && (
        <p
          className={`seller-notice seller-notice--${notice.type}`}
          role={notice.type === 'error' ? 'alert' : 'status'}
        >
          {notice.text}
        </p>
      )}
      {visible.map((product) => {
        const expanded = expandedProducts[product.id] !== false;
        const values = product.variants.map((v) => current(v));
        const uniform = values.every(
          (v) => normalized(v) === normalized(values[0])
        );
        const dirty =
          product.variants.some(
            (v) => normalized(current(v)) !== normalized(baseline(v))
          ) ||
          normalized(oldPriceValues[product.id]) !==
            normalized(oldBaseline(product));
        const max = Math.max(
          0,
          ...product.variants.map((v) =>
            normalized(current(v)) ? calcRetail(Number(current(v))) : 0
          )
        );
        const old = oldPriceValues[product.id] ?? '';
        const badOld =
          old !== '' && (!Number.isFinite(Number(old)) || Number(old) < max);
        return (
          <article
            className="seller-panel seller-price-product"
            key={product.id}
          >
            <div className="seller-price-heading">
              <div className="seller-cell-product">
                <ProductImage product={product} width={42} height={56} zoom />
                <span>
                  <strong>{product.title}</strong>
                  <small>
                    {product.sellerSku || product.publicArticle || product.platformSku} ·{' '}
                    {product.variants.length} SKU
                  </small>
                </span>
              </div>
              <div>
                <button
                  className="btn btn-outline"
                  disabled={!dirty || saving || invalidPrice || invalidOld}
                  onClick={() => save(product.id)}
                >
                  Сохранить товар
                </button>
                <button
                  className="seller-expand"
                  aria-label={`${expanded ? 'Свернуть' : 'Развернуть'} варианты ${product.title}`}
                  aria-expanded={expanded}
                  onClick={() =>
                    setExpandedProducts((prev) => ({
                      ...prev,
                      [product.id]: !expanded,
                    }))
                  }
                >
                  <Icon name="chevron" size={18} />
                </button>
              </div>
            </div>
            <div className="seller-price-shortcuts">
              <label>
                Единая цена всех размеров
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  disabled={saving}
                  placeholder={uniform ? 'Укажите цену' : 'Разные цены'}
                  aria-label={`Единая цена ${product.title}`}
                  value={uniform ? (values[0] ?? '') : ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setPriceValues((prev) => ({
                      ...prev,
                      ...Object.fromEntries(
                        product.variants.map((v) => [v.id, value])
                      ),
                    }));
                    setNotice(null);
                  }}
                />
              </label>
              <label>
                Старая цена для покупателя
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  disabled={saving}
                  aria-label={`Старая цена ${product.title}`}
                  aria-invalid={badOld}
                  value={old}
                  onChange={(e) => {
                    setOldPriceValues((prev) => ({
                      ...prev,
                      [product.id]: e.target.value,
                    }));
                    setNotice(null);
                  }}
                />
                {badOld && (
                  <small className="seller-field-error">
                    Не меньше {max} ₽
                  </small>
                )}
              </label>
              <p>
                <Icon name="tag" size={17} />
                Розничная цена рассчитывается платформой с учётом удержаний.
              </p>
            </div>
            {expanded && (
              <div className="seller-table-wrap">
                <table className="seller-data-table">
                  <thead>
                    <tr>
                      <th>Размер</th>
                      <th>Ваша цена, ₽</th>
                      <th>На витрине, ₽</th>
                      <th>Статус цены</th>
                    </tr>
                  </thead>
                  <tbody>
                    {product.variants.map((v) => {
                      const value = current(v);
                      const bad =
                        value !== '' &&
                        (!Number.isFinite(Number(value)) || Number(value) < 0);
                      const edited =
                        normalized(value) !== normalized(baseline(v));
                      return (
                        <tr key={v.id} className={edited ? 'is-edited' : ''}>
                          <td>{v.size}</td>
                          <td>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              disabled={saving}
                              aria-label={`Цена ${product.title}, ${v.size}`}
                              aria-invalid={bad}
                              value={value}
                              onChange={(e) => {
                                setPriceValues((prev) => ({
                                  ...prev,
                                  [v.id]: e.target.value,
                                }));
                                setNotice(null);
                              }}
                            />
                            {bad && (
                              <small className="seller-field-error">
                                Цена от 0 ₽
                              </small>
                            )}
                          </td>
                          <td className="seller-retail-price">
                            {normalized(value) > 0
                              ? calcRetail(Number(value)).toLocaleString(
                                  'ru-RU'
                                )
                              : '—'}
                          </td>
                          <td>
                            <span
                              className={`seller-status ${normalized(value) > 0 ? 'is-active' : ''}`}
                            >
                              {normalized(value) > 0
                                ? 'Цена задана'
                                : 'Без цены'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </article>
        );
      })}
      {!visible.length && (
        <div className="market-empty">
          <Icon name="tag" size={28} />
          <h3>
            {loading ? 'Загружаем цены…' : 'Нет товаров для редактирования'}
          </h3>
          <p>
            {loading
              ? 'Это займёт несколько секунд.'
              : 'Измените запрос или добавьте товары в ассортимент.'}
          </p>
        </div>
      )}
      <div className="seller-save-bar">
        <span>
          <Icon name={dirtyCount ? 'edit' : 'check'} size={18} />
          <span>
            {dirtyCount
              ? `Изменено полей: ${dirtyCount}`
              : 'Нет несохранённых изменений'}
            {(invalidPrice || invalidOld) && (
              <small>Исправьте цены в выделенных полях</small>
            )}
          </span>
        </span>
        <div>
          {dirtyCount > 0 && (
            <button
              className="btn btn-outline"
              disabled={saving}
              onClick={reset}
            >
              Сбросить
            </button>
          )}
          <button
            className="btn btn-primary"
            disabled={!dirtyCount || saving || invalidPrice || invalidOld}
            onClick={() => save()}
          >
            {saving ? 'Сохраняем…' : 'Сохранить цены'}
          </button>
        </div>
      </div>
    </section>
  );
}
